import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClipboardList, Filter, ChevronDown, Search, X, Loader2,
  ShieldAlert, CheckCircle2, Briefcase, Mail,
  Plus, Link2, Copy, Check, Power, Pencil, Video, ExternalLink,
  Trash2, ArrowUp, ArrowDown, Zap, Hand, FileText,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../contexts/UserProfileContext';
import { showToast } from '../utils/errorHandler';
import { listTemplates, cleanTemplateTitle, type InterviewTemplate } from '../api/interview';
import {
  RecruiterLoadingScreen,
  RecruiterPageShell,
} from '../components/Recruiter/RecruiterStates';
import {
  APPLY_FIELDS, APPLY_DOCUMENTS, APPLY_PRESETS, enabledCount,
  type ApplyFormConfig, type ApplyFieldMode,
} from '../lib/applyForm';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface Stage { key: string; label: string; }

interface Job {
  id: string;
  job_ref: string;
  title: string;
  location?: string;
  employment_type?: string;
  language?: string;
  warm_intro_enabled?: boolean;
  fach_interview_enabled?: boolean;
  auto_advance_threshold?: number | null;
  is_active?: boolean;
  application_count?: number;
  application_form_config?: ApplyFormConfig | null;
}

const emptyJobForm = {
  job_ref: '', title: '', description: '', location: '', employment_type: '',
  language: 'de', warm_intro_enabled: true, fach_interview_enabled: false,
  auto_advance_threshold: '75', is_active: true,
  application_form_config: {} as ApplyFormConfig,
};

interface AppRow {
  id: string;
  candidate_name?: string;
  candidate_email?: string;
  job_title?: string;
  job_ref?: string;
  stage: string;
  source?: string;
  created_at?: number;
  overall_fit_score?: number | null;
  recommendation?: string | null;
  recruiter_reviewed_at?: number | null;
  recruiter_override_score?: number | null;
  recruiter_override_recommendation?: string | null;
  final_score?: number | null;
  final_recommendation?: string | null;
  reviewed?: boolean;
  needs_human_review?: boolean;
  warm_intro_session_id?: string | null;
}

interface Criterion {
  label?: string; score?: number; weight?: number;
  justification?: string; evidence?: string[];
}

type StageKind = 'warm_intro' | 'fach_interview';
type GateMode = 'manual' | 'auto';

// Pipeline definition (job-level config, edited in the Jobs modal).
interface JobStage {
  id?: string;
  stage_key: string;
  label: string;
  kind: StageKind;
  template_id?: string | null;
  language?: string;
  gate_mode: GateMode;
  auto_threshold?: number | null;
  sort_order?: number;
}

// Pipeline instance for one application (status + score per stage).
interface PipelineStage {
  job_stage_id: string;
  stage_key: string;
  label: string;
  kind: StageKind;
  gate_mode: GateMode;
  auto_threshold?: number | null;
  sort_order: number;
  template_id?: string | null;
  language?: string;
  session_id?: string | null;
  status?: string | null;
  overall_score?: number | null;
  recommendation?: string | null;
  adhoc?: boolean;
}

interface AppDetail extends AppRow {
  language?: string;
  ats_provider?: string;
  criteria_scores?: Criterion[];
  summary?: string;
  strengths?: string;
  gaps?: string;
  missing_info?: string;
  detailed_feedback?: string;
  explanation_text?: string;
  ai_confidence?: number | string;
  model?: string;
  recruiter_notes?: string;
  warm_intro_session_id?: string | null;
  pipeline?: PipelineStage[];
  form_answers?: Record<string, string>;
  documents?: Record<string, { blob?: string; filename?: string; content_type?: string }>;
}

const FIELD_LABELS: Record<string, string> = Object.fromEntries(APPLY_FIELDS.map((f) => [f.key, f.de]));
const DOC_LABELS: Record<string, string> = Object.fromEntries(APPLY_DOCUMENTS.map((d) => [d.key, d.de]));

const STARTED_STATUSES = ['started', 'in_progress', 'completed'];

const RECS = ['strong_advance', 'advance', 'neutral', 'reject', 'strong_reject'];
const REC_STYLE: Record<string, string> = {
  strong_advance: 'text-emerald-700 bg-emerald-50',
  advance: 'text-blue-700 bg-blue-50',
  neutral: 'text-slate-600 bg-slate-100',
  reject: 'text-red-700 bg-red-50',
  strong_reject: 'text-red-800 bg-red-100',
};

const RecBadge: React.FC<{ rec?: string | null }> = ({ rec }) => {
  if (!rec) return <span className="text-[11px] font-bold uppercase text-slate-300">-</span>;
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${REC_STYLE[rec] || 'text-slate-600 bg-slate-100'}`}>
      {rec.replace('_', ' ')}
    </span>
  );
};

const initials = (name?: string) => {
  if (!name) return '–';
  const parts = name.trim().split(/\s+/);
  return (((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase()) || '–';
};

const WARM_INTRO_STATUS_LABEL: Record<string, string> = {
  invited: 'Eingeladen',
  account_linked: 'Eingeladen',
  consent_given: 'Bereit',
  started: 'Gestartet',
  in_progress: 'Im Gespräch',
  completed: 'Abgeschlossen',
  expired: 'Abgelaufen',
  cancelled: 'Abgebrochen',
};

const Applications: React.FC = () => {
  const navigate = useNavigate();
  const { apiCall, user, authReady } = useAuth();
  const { userProfile } = useUserProfile();
  const isRecruiter = !!userProfile?.is_recruiter;

  const [stages, setStages] = useState<Stage[]>([]);
  const [apps, setApps] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [jobFilter, setJobFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [detail, setDetail] = useState<AppDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'app' | 'interviews'>('app');

  // Pipeline (Layer 3): per-stage manual invite/resend in the drawer.
  const [stageBusy, setStageBusy] = useState<string | null>(null);
  const [stageInviteUrl, setStageInviteUrl] = useState<Record<string, string>>({});
  const [copiedStage, setCopiedStage] = useState<string | null>(null);
  const [form, setForm] = useState({ stage: '', override_recommendation: '', override_score: '', recruiter_notes: '' });

  // Ad-hoc invite: invite to any template without a configured stage.
  const [showAdhoc, setShowAdhoc] = useState(false);
  const [adhocTemplate, setAdhocTemplate] = useState('');
  const [adhocBusy, setAdhocBusy] = useState(false);

  // Jobs / Career-Links
  const [jobsOpen, setJobsOpen] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [jobForm, setJobForm] = useState(emptyJobForm);
  const [editingJobRef, setEditingJobRef] = useState<string | null>(null);
  const [jobFormLoading, setJobFormLoading] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [jobSaving, setJobSaving] = useState(false);
  const [copiedRef, setCopiedRef] = useState<string | null>(null);

  // Pipeline builder (per-job interview stages).
  const [templates, setTemplates] = useState<InterviewTemplate[]>([]);
  const [jobStages, setJobStages] = useState<JobStage[]>([]);
  const [stagesLoading, setStagesLoading] = useState(false);

  const warmIntroTemplates = useMemo(
    () => templates.filter((t) => t.family_key === 'cevee_prescreen_intro'),
    [templates],
  );
  const fachTemplates = useMemo(
    () => templates.filter((t) => t.family_key !== 'cevee_prescreen_intro'),
    [templates],
  );
  // Split the Fach picker so the recruiter's own templates don't get lost
  // among the (larger) Cevee library.
  const isOwnTemplate = (t: InterviewTemplate) =>
    !!t.company_id && !!companyId && String(t.company_id) === String(companyId);
  const ownFachTemplates = useMemo(
    () => fachTemplates.filter(isOwnTemplate),
    [fachTemplates, companyId], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const ceveeFachTemplates = useMemo(
    () => fachTemplates.filter((t) => !isOwnTemplate(t)),
    [fachTemplates, companyId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const loadTemplates = async () => {
    try {
      const { templates: tpls } = await listTemplates(apiCall, { includePublic: true, onlyActive: true });
      setTemplates(tpls || []);
    } catch { /* ignore */ }
  };

  const loadJobStages = async (jobRef: string) => {
    setStagesLoading(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/jobs/${encodeURIComponent(jobRef)}/stages`);
      if (resp.ok) {
        const data = await resp.json();
        setJobStages((data.stages || []) as JobStage[]);
      } else {
        setJobStages([]);
      }
    } catch {
      setJobStages([]);
    }
    setStagesLoading(false);
  };

  const nextFachKey = () => {
    let n = 0;
    for (const s of jobStages) {
      const m = /^fach_(\d+)$/.exec(s.stage_key);
      if (m) n = Math.max(n, Number(m[1]));
    }
    return `fach_${n + 1}`;
  };

  const addStage = (kind: StageKind) => {
    if (kind === 'warm_intro' && jobStages.some((s) => s.kind === 'warm_intro')) return;
    const defTpl = kind === 'warm_intro'
      ? (warmIntroTemplates.find((t) => t.language === jobForm.language) || warmIntroTemplates[0])
      : undefined;
    const fachCount = jobStages.filter((s) => s.kind === 'fach_interview').length;
    setJobStages((prev) => [
      ...prev,
      {
        stage_key: kind === 'warm_intro' ? 'warm_intro' : nextFachKey(),
        label: kind === 'warm_intro' ? 'Warm Intro' : `Fach-Interview ${fachCount + 1}`,
        kind,
        template_id: defTpl?.id || null,
        language: defTpl?.language || jobForm.language || 'de',
        gate_mode: 'manual',
        auto_threshold: null,
      },
    ]);
  };

  const updateStage = (idx: number, patch: Partial<JobStage>) => {
    setJobStages((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeStage = (idx: number) => {
    setJobStages((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveStage = (idx: number, dir: -1 | 1) => {
    setJobStages((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const saveJobStages = async (jobRef: string) => {
    const payload = jobStages.map((s, i) => {
      let templateId = s.template_id || null;
      if (!templateId && s.kind === 'warm_intro') {
        const def = warmIntroTemplates.find((t) => t.language === (s.language || jobForm.language))
          || warmIntroTemplates[0];
        templateId = def?.id || null;
      }
      return {
        stage_key: s.stage_key,
        label: s.label,
        kind: s.kind,
        template_id: templateId,
        language: s.language || 'de',
        gate_mode: s.gate_mode,
        auto_threshold: s.gate_mode === 'auto' ? (s.auto_threshold ?? 75) : null,
        sort_order: i,
      };
    });
    const resp = await apiCall(`${API_BASE}/api/v1/prescreen/jobs/${encodeURIComponent(jobRef)}/stages`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stages: payload }),
    });
    if (!resp.ok) throw new Error('stages save failed');
  };

  useEffect(() => {
    if (!user || !authReady || !isRecruiter) return;
    loadBoard();
    loadTemplates();
    loadCompanyId();
  }, [user?.userId, authReady, isRecruiter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve company_id once so the ad-hoc picker can group the recruiter's
  // own templates without forcing them to open the Jobs modal first.
  const loadCompanyId = async () => {
    if (companyId) return;
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/jobs`);
      if (resp.ok) {
        const data = await resp.json();
        if (data.company_id) setCompanyId(data.company_id);
      }
    } catch { /* ignore */ }
  };

  const loadBoard = async () => {
    setLoading(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/applications`);
      if (resp.ok) {
        const data = await resp.json();
        setApps(data.applications || []);
        setStages(data.stages || []);
      } else if (resp.status === 404) {
        setApps([]);
        setStages([]);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setDetail({ id, stage: '' });
    setActiveTab('app');
    setStageInviteUrl({});
    setCopiedStage(null);
    setStageBusy(null);
    setShowAdhoc(false);
    setAdhocTemplate('');
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/applications/${id}`);
      if (resp.ok) {
        const data = await resp.json();
        const d: AppDetail = data.application;
        setDetail(d);
        setForm({
          stage: d.stage || '',
          override_recommendation: d.recruiter_override_recommendation || '',
          override_score: d.recruiter_override_score != null ? String(d.recruiter_override_score) : '',
          recruiter_notes: d.recruiter_notes || '',
        });
      } else {
        showToast('Bewerbung konnte nicht geladen werden', 'error');
        setDetail(null);
      }
    } catch {
      setDetail(null);
    }
    setDetailLoading(false);
  };

  const saveReview = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        stage: form.stage || undefined,
        override_recommendation: form.override_recommendation || undefined,
        override_score: form.override_score.trim() ? Number(form.override_score) : undefined,
        recruiter_notes: form.recruiter_notes || undefined,
      };
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/applications/${detail.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        const data = await resp.json();
        setDetail(data.application);
        showToast('Gespeichert', 'success');
        loadBoard();
      } else {
        showToast('Speichern fehlgeschlagen', 'error');
      }
    } catch {
      showToast('Speichern fehlgeschlagen', 'error');
    }
    setSaving(false);
  };

  // Generic per-stage invite/resend. The backend reuses an open session
  // (resend) or creates a new interview, links it to the application stage,
  // and advances the coarse board stage. Works for Warm Intro AND every
  // Fach-Interview in the pipeline.
  const inviteStage = async (stageKey: string) => {
    if (!detail) return;
    if (!detail.candidate_email) { showToast('Keine Bewerber-E-Mail vorhanden', 'error'); return; }
    setStageBusy(stageKey);
    try {
      const resp = await apiCall(
        `${API_BASE}/api/v1/prescreen/applications/${detail.id}/stages/${encodeURIComponent(stageKey)}/invite`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({} as Record<string, unknown>));
        const d = (err as { detail?: unknown }).detail;
        const msg = typeof d === 'string' ? d
          : (d && typeof d === 'object' && 'message' in d ? String((d as { message: unknown }).message) : 'Einladung fehlgeschlagen');
        showToast(msg, 'error');
        return;
      }
      const data = await resp.json();
      if (data.application) setDetail(data.application);
      const url = data.invite?.invite_url as string | undefined;
      if (url) setStageInviteUrl((prev) => ({ ...prev, [stageKey]: url }));
      showToast('Einladung verschickt', 'success');
      loadBoard();
    } catch {
      showToast('Einladung fehlgeschlagen', 'error');
    } finally {
      setStageBusy(null);
    }
  };

  // Ad-hoc invite: any template, no configured stage required. Lets the
  // recruiter add a one-off Fach-Interview or invite for jobs without a
  // pipeline. The new interview shows up in the pipeline as an ad-hoc stage.
  const inviteAdhoc = async () => {
    if (!detail || !adhocTemplate) return;
    if (!detail.candidate_email) { showToast('Keine Bewerber-E-Mail vorhanden', 'error'); return; }
    setAdhocBusy(true);
    try {
      const resp = await apiCall(
        `${API_BASE}/api/v1/prescreen/applications/${detail.id}/invite-adhoc`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ template_id: adhocTemplate }) },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({} as Record<string, unknown>));
        const d = (err as { detail?: unknown }).detail;
        const msg = typeof d === 'string' ? d
          : (d && typeof d === 'object' && 'message' in d ? String((d as { message: unknown }).message) : 'Einladung fehlgeschlagen');
        showToast(msg, 'error');
        return;
      }
      const data = await resp.json();
      if (data.application) setDetail(data.application);
      const url = data.invite?.invite_url as string | undefined;
      const sk = data.invite?.stage_key as string | undefined;
      if (url && sk) setStageInviteUrl((prev) => ({ ...prev, [sk]: url }));
      showToast('Einladung verschickt', 'success');
      setShowAdhoc(false);
      setAdhocTemplate('');
      loadBoard();
    } catch {
      showToast('Einladung fehlgeschlagen', 'error');
    } finally {
      setAdhocBusy(false);
    }
  };

  const loadJobs = async () => {
    setJobsLoading(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/jobs`);
      if (resp.ok) {
        const data = await resp.json();
        setJobs(data.jobs || []);
        if (data.company_id) setCompanyId(data.company_id);
      }
    } catch { /* ignore */ }
    setJobsLoading(false);
  };

  const openJobs = () => {
    setJobsOpen(true);
    setShowJobForm(false);
    setEditingJobRef(null);
    setJobForm(emptyJobForm);
    setJobStages([]);
    loadJobs();
    loadTemplates();
  };

  const startNewJob = () => {
    setEditingJobRef(null);
    setJobForm(emptyJobForm);
    setJobStages([{
      stage_key: 'warm_intro', label: 'Warm Intro', kind: 'warm_intro',
      template_id: null, language: emptyJobForm.language, gate_mode: 'manual', auto_threshold: null,
    }]);
    setShowJobForm(true);
  };

  const cancelJobForm = () => {
    setShowJobForm(false);
    setEditingJobRef(null);
    setJobForm(emptyJobForm);
    setJobStages([]);
  };

  const openEditJob = async (jobRef: string) => {
    setJobFormLoading(true);
    setShowJobForm(true);
    setEditingJobRef(jobRef);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/jobs/${encodeURIComponent(jobRef)}`);
      if (!resp.ok) {
        showToast('Job konnte nicht geladen werden', 'error');
        cancelJobForm();
        setJobFormLoading(false);
        return;
      }
      const data = await resp.json();
      const j = data.job;
      setJobForm({
        job_ref: j.job_ref || '',
        title: j.title || '',
        description: j.description || '',
        location: j.location || '',
        employment_type: j.employment_type || '',
        language: j.language || 'de',
        warm_intro_enabled: j.warm_intro_enabled !== false,
        fach_interview_enabled: !!j.fach_interview_enabled,
        auto_advance_threshold: j.auto_advance_threshold != null ? String(j.auto_advance_threshold) : '',
        is_active: j.is_active !== false,
        application_form_config: (j.application_form_config || {}) as ApplyFormConfig,
      });
      await loadJobStages(jobRef);
    } catch {
      showToast('Job konnte nicht geladen werden', 'error');
      cancelJobForm();
    }
    setJobFormLoading(false);
  };

  // ── Configurable apply-form (extra fields/documents per job) ──
  const formCfg = (jobForm.application_form_config || {}) as ApplyFormConfig;
  const cfgMode = (section: 'fields' | 'documents', key: string): 'off' | ApplyFieldMode =>
    (formCfg[section]?.[key]) || 'off';
  const setCfgMode = (section: 'fields' | 'documents', key: string, mode: 'off' | ApplyFieldMode) => {
    setJobForm((prev) => {
      const c: ApplyFormConfig = { ...(prev.application_form_config || {}) };
      const sec: Record<string, ApplyFieldMode> = { ...((c[section] as any) || {}) };
      if (mode === 'off') delete sec[key]; else sec[key] = mode;
      if (Object.keys(sec).length) c[section] = sec; else delete c[section];
      return { ...prev, application_form_config: c };
    });
  };
  const applyFormPreset = (preset: 'students' | 'pupils') => {
    const p = APPLY_PRESETS[preset];
    setJobForm((prev) => {
      const c: ApplyFormConfig = { ...(prev.application_form_config || {}) };
      const fields: Record<string, ApplyFieldMode> = { ...(c.fields || {}) };
      const documents: Record<string, ApplyFieldMode> = { ...(c.documents || {}) };
      p.fields.forEach((k) => { fields[k] = fields[k] || 'optional'; });
      p.documents.forEach((k) => { documents[k] = documents[k] || 'optional'; });
      c.fields = fields; c.documents = documents;
      return { ...prev, application_form_config: c };
    });
  };
  const clearFormConfig = () =>
    setJobForm((prev) => ({ ...prev, application_form_config: {} as ApplyFormConfig }));

  const downloadDocument = async (appId: string, key: string, filename?: string) => {
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/applications/${appId}/documents/${key}`);
      if (!resp.ok) { showToast('Dokument konnte nicht geladen werden', 'error'); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      if (filename) a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      showToast('Dokument konnte nicht geladen werden', 'error');
    }
  };

  const toggleJobActive = async (j: Job) => {
    const makeActive = !j.is_active;
    const label = makeActive ? 'reaktiviert' : 'deaktiviert';
    if (!makeActive && (j.application_count ?? 0) > 0) {
      const ok = window.confirm(
        `"${j.title}" hat ${j.application_count} Bewerbung(en). Der Career-Link wird offline. Bestehende Bewerbungen bleiben im Board. Fortfahren?`,
      );
      if (!ok) return;
    }
    setJobSaving(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/jobs/${encodeURIComponent(j.job_ref)}`);
      if (!resp.ok) { showToast('Aktion fehlgeschlagen', 'error'); setJobSaving(false); return; }
      const { job } = await resp.json();
      const upsert = await apiCall(`${API_BASE}/api/v1/prescreen/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_ref: job.job_ref,
          title: job.title,
          description: job.description,
          location: job.location,
          employment_type: job.employment_type,
          language: job.language || 'de',
          warm_intro_enabled: job.warm_intro_enabled !== false,
          fach_interview_enabled: !!job.fach_interview_enabled,
          auto_advance_threshold: job.auto_advance_threshold,
          is_active: makeActive,
          application_form_config: job.application_form_config || {},
        }),
      });
      if (upsert.ok) {
        showToast(`Job ${label}`, 'success');
        loadJobs();
      } else {
        showToast('Aktion fehlgeschlagen', 'error');
      }
    } catch {
      showToast('Aktion fehlgeschlagen', 'error');
    }
    setJobSaving(false);
  };

  const saveJob = async () => {
    if (!jobForm.job_ref.trim() || !jobForm.title.trim() || !jobForm.description.trim()) {
      showToast('Job-Ref, Titel und Beschreibung sind erforderlich', 'error');
      return;
    }
    setJobSaving(true);
    try {
      // Warm-intro toggle and auto-advance threshold are no longer separate
      // job fields: the pipeline builder is the single source of truth. Derive
      // the legacy columns from it so the board summary badges stay correct.
      const hasWarmIntro = jobStages.some((s) => s.kind === 'warm_intro');
      const firstAuto = jobStages.find((s) => s.gate_mode === 'auto');
      const body = {
        ...jobForm,
        warm_intro_enabled: hasWarmIntro,
        auto_advance_threshold: firstAuto ? (firstAuto.auto_threshold ?? 75) : null,
        is_active: jobForm.is_active,
      };
      const resp = await apiCall(`${API_BASE}/api/v1/prescreen/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.company_id) setCompanyId(data.company_id);
        try {
          await saveJobStages(jobForm.job_ref.trim());
        } catch {
          showToast('Job gespeichert, aber Pipeline konnte nicht gespeichert werden', 'error');
        }
        showToast(editingJobRef ? 'Job aktualisiert' : 'Job angelegt', 'success');
        cancelJobForm();
        loadJobs();
      } else {
        showToast('Speichern fehlgeschlagen', 'error');
      }
    } catch {
      showToast('Speichern fehlgeschlagen', 'error');
    }
    setJobSaving(false);
  };

  const careerLink = (jobRef: string) =>
    `${window.location.origin}/apply/${companyId}/${jobRef}`;

  const copyLink = (jobRef: string) => {
    navigator.clipboard?.writeText(careerLink(jobRef)).then(() => {
      setCopiedRef(jobRef);
      setTimeout(() => setCopiedRef(null), 1800);
    }).catch(() => showToast('Kopieren fehlgeschlagen', 'error'));
  };

  const allJobs = useMemo(() => {
    const jobs = new Set<string>();
    apps.forEach((a) => { if (a.job_title) jobs.add(a.job_title); });
    return Array.from(jobs).sort();
  }, [apps]);

  const filtered = useMemo(() => {
    let result = apps;
    if (jobFilter !== 'all') result = result.filter((a) => a.job_title === jobFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((a) =>
        a.candidate_name?.toLowerCase().includes(q) ||
        a.candidate_email?.toLowerCase().includes(q) ||
        a.job_title?.toLowerCase().includes(q));
    }
    return result;
  }, [apps, jobFilter, searchQuery]);

  const byStage = useMemo(() => {
    const groups: Record<string, AppRow[]> = {};
    for (const s of stages) groups[s.key] = [];
    for (const a of filtered) (groups[a.stage] ||= []).push(a);
    return groups;
  }, [filtered, stages]);

  // Auth, signed-in and recruiter checks are handled by the RecruiterGuard
  // wrapper before this mounts (see App.tsx). Only gate on data loading here so
  // it's a single continuous loader (matches InterviewHub), not a double flash.
  if (loading) return <RecruiterLoadingScreen icon={ClipboardList} />;

  return (
    <RecruiterPageShell wide>
      {jobsOpen && (
        <div className="fixed inset-0 z-[10001] flex justify-end bg-black/40 backdrop-blur-sm" onClick={() => setJobsOpen(false)}>
          <div className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Jobs & Career-Links</h2>
                <p className="text-xs text-slate-500">Job anlegen, Link teilen, Bewerbungen werden automatisch gescreent.</p>
              </div>
              <button onClick={() => setJobsOpen(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              {!showJobForm && (
                <button
                  onClick={startNewJob}
                  className="mb-4 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4" /> Neuen Job anlegen
                </button>
              )}

              {showJobForm && (
                <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <h3 className="mb-3 text-sm font-semibold text-slate-800">
                    {editingJobRef ? 'Job bearbeiten' : 'Neuer Job'}
                  </h3>
                  {jobFormLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                  ) : (
                  <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="text-xs font-medium text-slate-600">
                      Titel *
                      <input value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10" placeholder="z. B. Infrastructure Engineer" />
                    </label>
                    <label className="text-xs font-medium text-slate-600">
                      Job-Ref * (eindeutig, für den Link)
                      <input
                        value={jobForm.job_ref}
                        onChange={(e) => setJobForm({ ...jobForm, job_ref: e.target.value.replace(/[^a-zA-Z0-9-_]/g, '') })}
                        disabled={!!editingJobRef}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10 disabled:bg-slate-100 disabled:text-slate-500"
                        placeholder="infra-eng-2026"
                      />
                      {editingJobRef && (
                        <span className="mt-0.5 block text-[10px] text-slate-400">Slug ist fest. Der Career-Link bleibt gleich.</span>
                      )}
                    </label>
                    <label className="text-xs font-medium text-slate-600">
                      Standort
                      <input value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10" placeholder="München / Remote" />
                    </label>
                    <label className="text-xs font-medium text-slate-600">
                      Anstellungsart
                      <input value={jobForm.employment_type} onChange={(e) => setJobForm({ ...jobForm, employment_type: e.target.value })}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10" placeholder="Vollzeit" />
                    </label>
                  </div>
                  <label className="mt-3 block text-xs font-medium text-slate-600">
                    Job-Beschreibung * (Grundlage fürs KI-Screening)
                    <textarea value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                      rows={6} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10" placeholder="Aufgaben, Anforderungen, Must-haves ..." />
                  </label>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="text-xs font-medium text-slate-600">
                      Sprache
                      <select value={jobForm.language} onChange={(e) => setJobForm({ ...jobForm, language: e.target.value })}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10">
                        <option value="de">Deutsch</option>
                        <option value="en">English</option>
                      </select>
                    </label>
                  </div>

                  {/* Interview pipeline: collapsible so the job form stays scannable */}
                  <div className="mt-3">
                  <Collapsible
                    title="Interview-Pipeline"
                    subtitle={
                      jobStages.length === 0
                        ? 'Optional: Warm Intro & Fach-Interviews nach dem Screening'
                        : `${jobStages.length} Stage${jobStages.length === 1 ? '' : 's'} · ${jobStages.some((s) => s.gate_mode === 'auto') ? 'Auto + Manuell' : 'Manuell'}`
                    }
                    defaultOpen={jobStages.length > 0}
                  >
                    {stagesLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {jobStages.length === 0 && (
                          <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center text-[12px] text-slate-400">
                            Keine Stages. Füge Warm Intro oder ein Fach-Interview hinzu.
                          </p>
                        )}
                        {jobStages.map((s, idx) => {
                          const opts = s.kind === 'warm_intro' ? warmIntroTemplates : fachTemplates;
                          return (
                            <div key={s.stage_key} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                              <div className="flex items-center gap-2">
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">{idx + 1}</span>
                                <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase ${s.kind === 'warm_intro' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'}`}>
                                  {s.kind === 'warm_intro' ? 'Warm Intro' : 'Fach'}
                                </span>
                                <input
                                  value={s.label}
                                  onChange={(e) => updateStage(idx, { label: e.target.value })}
                                  className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1 text-[12px] text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                                  placeholder="Bezeichnung"
                                />
                                <button type="button" onClick={() => moveStage(idx, -1)} disabled={idx === 0}
                                  className="rounded p-1 text-slate-400 hover:bg-slate-200 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                                <button type="button" onClick={() => moveStage(idx, 1)} disabled={idx === jobStages.length - 1}
                                  className="rounded p-1 text-slate-400 hover:bg-slate-200 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                                <button type="button" onClick={() => removeStage(idx)}
                                  className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>

                              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <select
                                  value={s.template_id || ''}
                                  onChange={(e) => {
                                    const tpl = opts.find((t) => t.id === e.target.value);
                                    updateStage(idx, { template_id: e.target.value || null, language: tpl?.language || s.language });
                                  }}
                                  className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-[12px] text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                                >
                                  <option value="">Template wählen…</option>
                                  {s.kind === 'warm_intro' ? (
                                    warmIntroTemplates.map((t) => (
                                      <option key={t.id} value={t.id}>
                                        {cleanTemplateTitle(t.title)} · {t.language?.toUpperCase()}
                                      </option>
                                    ))
                                  ) : (
                                    <>
                                      {ownFachTemplates.length > 0 && (
                                        <optgroup label="★ Meine Templates">
                                          {ownFachTemplates.map((t) => (
                                            <option key={t.id} value={t.id}>
                                              {cleanTemplateTitle(t.title)} · {t.language?.toUpperCase()}
                                            </option>
                                          ))}
                                        </optgroup>
                                      )}
                                      {ceveeFachTemplates.length > 0 && (
                                        <optgroup label="Cevee-Bibliothek">
                                          {ceveeFachTemplates.map((t) => (
                                            <option key={t.id} value={t.id}>
                                              {cleanTemplateTitle(t.title)} · {t.language?.toUpperCase()}
                                            </option>
                                          ))}
                                        </optgroup>
                                      )}
                                    </>
                                  )}
                                </select>

                                <div className="flex items-center gap-2">
                                  <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
                                    {(['manual', 'auto'] as const).map((g) => (
                                      <button
                                        key={g}
                                        type="button"
                                        onClick={() => updateStage(idx, { gate_mode: g, auto_threshold: g === 'auto' ? (s.auto_threshold ?? 75) : null })}
                                        className={`inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-semibold transition ${
                                          s.gate_mode === g ? (g === 'auto' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-white') : 'bg-white text-slate-600 hover:bg-slate-50'
                                        }`}
                                      >
                                        {g === 'auto' ? <Zap className="h-3 w-3" /> : <Hand className="h-3 w-3" />}
                                        {g === 'auto' ? 'Auto' : 'Manuell'}
                                      </button>
                                    ))}
                                  </div>
                                  {s.gate_mode === 'auto' && (
                                    <label className="flex items-center gap-1 text-[11px] text-slate-500">
                                      ab Score
                                      <input
                                        type="number" min={0} max={100}
                                        value={s.auto_threshold ?? 75}
                                        onChange={(e) => updateStage(idx, { auto_threshold: e.target.value ? Number(e.target.value) : null })}
                                        className="w-14 rounded-md border border-slate-200 px-1.5 py-1 text-[12px] text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                                      />
                                    </label>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        <div className="flex flex-wrap gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => addStage('warm_intro')}
                            disabled={jobStages.some((s) => s.kind === 'warm_intro')}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-40"
                          >
                            <Plus className="h-3 w-3" /> Warm Intro
                          </button>
                          <button
                            type="button"
                            onClick={() => addStage('fach_interview')}
                            className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-100"
                          >
                            <Plus className="h-3 w-3" /> Fach-Interview
                          </button>
                        </div>
                        <p className="text-[11px] leading-relaxed text-slate-400">
                          Manuell = Recruiter lädt ein. Auto = System lädt bei positivem Score ein. Absagen bleiben immer manuell.
                        </p>
                      </div>
                    )}
                  </Collapsible>
                  </div>

                  {/* Extra apply-form fields/documents: optional, shown on the public apply page */}
                  <div className="mt-3">
                  <Collapsible
                    title="Zusätzliche Bewerbungsangaben"
                    subtitle={
                      enabledCount(formCfg) === 0
                        ? 'Optional: Uni/Schule, Verfügbarkeit, Dokumente abfragen'
                        : `${enabledCount(formCfg)} Feld${enabledCount(formCfg) === 1 ? '' : 'er'} aktiv`
                    }
                    defaultOpen={enabledCount(formCfg) > 0}
                  >
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-medium text-slate-500">Schnellauswahl:</span>
                        <button type="button" onClick={() => applyFormPreset('students')}
                          className="rounded-md border border-pink-200 bg-pink-50 px-2.5 py-1 text-[11px] font-semibold text-pink-700 hover:bg-pink-100">
                          {APPLY_PRESETS.students.label}
                        </button>
                        <button type="button" onClick={() => applyFormPreset('pupils')}
                          className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 hover:bg-amber-100">
                          {APPLY_PRESETS.pupils.label}
                        </button>
                        {enabledCount(formCfg) > 0 && (
                          <button type="button" onClick={clearFormConfig}
                            className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                            Leeren
                          </button>
                        )}
                      </div>

                      <div>
                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Angaben</p>
                        <div className="space-y-1.5">
                          {APPLY_FIELDS.map((f) => (
                            <FormFieldToggle key={f.key} label={f.de} mode={cfgMode('fields', f.key)}
                              onChange={(m) => setCfgMode('fields', f.key, m)} />
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Dokumente</p>
                        <div className="space-y-1.5">
                          {APPLY_DOCUMENTS.map((d) => (
                            <FormFieldToggle key={d.key} label={d.de} mode={cfgMode('documents', d.key)}
                              onChange={(m) => setCfgMode('documents', d.key, m)} />
                          ))}
                        </div>
                      </div>

                      <p className="text-[11px] leading-relaxed text-slate-400">
                        Wird nur auf der Bewerbungsseite gezeigt, wenn aktiviert. Optional/Pflicht steuert die Pflichtangabe. Dokumente & Angaben sind nur für deine Sichtung und fließen <span className="font-medium">nicht</span> ins KI-Screening ein.
                      </p>
                    </div>
                  </Collapsible>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <button onClick={saveJob} disabled={jobSaving || jobFormLoading}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
                      {jobSaving && <Loader2 className="h-4 w-4 animate-spin" />} Speichern
                    </button>
                    <button onClick={cancelJobForm} className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100">Abbrechen</button>
                  </div>
                  </>
                  )}
                </div>
              )}

              {jobsLoading ? (
                <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></div>
              ) : jobs.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center">
                  <Briefcase className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                  <p className="text-sm text-slate-500">Noch keine Jobs. Lege oben den ersten an.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {jobs.map((j) => (
                    <div key={j.id} className={`rounded-xl border p-4 ${j.is_active ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50/80 opacity-90'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-slate-900">{j.title}</span>
                            {j.is_active ? (
                              <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-600">aktiv</span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-400"><Power className="h-2.5 w-2.5" />inaktiv</span>
                            )}
                          </div>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {[
                              j.job_ref,
                              j.location,
                              `${j.application_count ?? 0} Bewerbung${(j.application_count ?? 0) === 1 ? '' : 'en'}`,
                              j.language === 'en' ? 'EN' : 'DE',
                              j.employment_type,
                            ].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            onClick={() => openEditJob(j.job_ref)}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                          >
                            <Pencil className="h-3 w-3" /> Bearbeiten
                          </button>
                          <button
                            onClick={() => toggleJobActive(j)}
                            disabled={jobSaving}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                          >
                            <Power className="h-3 w-3" /> {j.is_active ? 'Deaktivieren' : 'Reaktivieren'}
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                        <Link2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="flex-1 truncate font-mono text-xs text-slate-600">{careerLink(j.job_ref)}</span>
                        <button onClick={() => copyLink(j.job_ref)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-600 shadow-sm hover:text-slate-900">
                          {copiedRef === j.job_ref ? <><Check className="h-3 w-3 text-emerald-500" /> kopiert</> : <><Copy className="h-3 w-3" /> kopieren</>}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="mb-5 text-sm text-slate-500">
        KI-Vorauswahl eingehender Bewerbungen. Absagen erfordern menschliche Prüfung (EU AI Act / DSGVO Art. 22).
      </p>

      {/* Toolbar */}
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <div className="relative">
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            className="appearance-none cursor-pointer rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-8 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-900/10"
          >
            <option value="all">Alle Jobs ({allJobs.length})</option>
            {allJobs.map((j) => (
              <option key={j} value={j}>{j} ({apps.filter((a) => a.job_title === j).length})</option>
            ))}
          </select>
          <Filter className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        </div>
        <div className="relative max-w-xs flex-1">
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Bewerber suchen..."
            className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-sm text-slate-700 placeholder-slate-400 hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-900/10"
          />
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-slate-500">
            <span className="font-semibold text-slate-700">{filtered.length}</span> Bewerbungen
          </span>
          <button
            onClick={openJobs}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <Link2 className="h-3.5 w-3.5" /> Jobs & Career-Links
          </button>
        </div>
      </div>

      {apps.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center">
          <ClipboardList className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Noch keine Bewerbungen</p>
          <p className="mt-1 text-xs text-slate-400">Bewerbungen aus dem Career-Link oder ATS-Sync erscheinen hier.</p>
        </div>
      ) : (
        <div className="-mx-4 overflow-x-auto px-4 pb-4">
          <div className="flex min-w-max gap-4">
            {stages.map((stage) => {
              const items = byStage[stage.key] || [];
              const hasItems = items.length > 0;
              return (
                <div key={stage.key} className={`w-[270px] shrink-0 rounded-xl border ${hasItems ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50/80'}`}>
                  <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3">
                    <span className="text-[13px] font-semibold tracking-tight text-slate-800">{stage.label}</span>
                    <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-bold ${hasItems ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-400'}`}>
                      {items.length}
                    </span>
                  </div>
                  <div className="max-h-[calc(100vh-260px)] space-y-2 overflow-y-auto px-2 pb-2 pt-2">
                    {items.length === 0 ? (
                      <div className="py-8 text-center"><p className="text-[11px] text-slate-400">-</p></div>
                    ) : items.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => openDetail(a.id)}
                        className="block w-full rounded-xl border border-slate-200/80 bg-white p-3 text-left shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all hover:border-slate-300 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
                      >
                        <div className="mb-1 flex items-center gap-1.5">
                          {a.reviewed && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-600">
                              <CheckCircle2 className="h-2.5 w-2.5" /> geprüft
                            </span>
                          )}
                          {a.needs_human_review && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-600">
                              <ShieldAlert className="h-2.5 w-2.5" /> human review
                            </span>
                          )}
                          {a.source && (
                            <span className="ml-auto text-[9px] font-bold uppercase text-slate-400">{a.source}</span>
                          )}
                        </div>
                        <p className="truncate text-[13px] font-semibold leading-snug text-slate-800">{a.candidate_name || '-'}</p>
                        {a.job_title && (
                          <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-slate-500">
                            <Briefcase className="h-2.5 w-2.5 shrink-0" />{a.job_title}
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-lg font-bold text-slate-800">
                            {a.final_score != null ? a.final_score : '-'}
                          </span>
                          <span className="text-[11px] text-slate-400">/100</span>
                          <span className="ml-auto"><RecBadge rec={a.final_recommendation} /></span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail drawer */}
      <AnimatePresence>
        {detail && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10001] flex justify-end bg-black/40 backdrop-blur-sm"
            onClick={() => setDetail(null)}
          >
            <motion.div
              initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-slate-50 shadow-2xl"
            >
              <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-sm font-bold text-white">
                  {initials(detail.candidate_name)}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-base font-bold leading-tight text-slate-900">{detail.candidate_name || 'Bewerbung'}</h2>
                  {detail.job_title && <p className="truncate text-xs text-slate-500">{detail.job_title}</p>}
                </div>
                <button onClick={() => setDetail(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {detailLoading ? (
                <div className="flex flex-1 items-center justify-center py-20">
                  <Loader2 className="h-7 w-7 animate-spin text-pink-500" />
                </div>
              ) : (
                <div className="space-y-4 p-5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    {detail.candidate_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{detail.candidate_email}</span>}
                    {detail.job_title && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{detail.job_title}</span>}
                    {detail.source && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold uppercase">{detail.source}</span>}
                  </div>

                  {detail.needs_human_review && (
                    <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-[13px] text-red-700">
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                      <span><b>Human Review erforderlich.</b> Die KI empfiehlt eine Absage. Vor einer endgültigen Entscheidung muss ein Mensch prüfen (DSGVO Art. 22 / EU AI Act).</span>
                    </div>
                  )}

                  {/* Hero: Score + Empfehlung – immer sichtbar */}
                  <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Fit Score</span>
                      <span className="text-4xl font-extrabold leading-none text-slate-900">
                        {detail.final_score != null ? detail.final_score : '-'}
                        <span className="text-base font-semibold text-slate-300">/100</span>
                      </span>
                    </div>
                    <div className="flex flex-col items-start gap-1.5">
                      <RecBadge rec={detail.final_recommendation} />
                      {detail.ai_confidence != null && (
                        <span className="text-[11px] text-slate-400">
                          KI-Konfidenz {String(detail.ai_confidence)}
                        </span>
                      )}
                    </div>
                    {detail.reviewed && (
                      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-600">
                        <CheckCircle2 className="h-3.5 w-3.5" /> geprüft
                      </span>
                    )}
                  </div>

                  {/* Tabs: Bewerbung vs Interviews */}
                  <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1">
                    {([
                      { key: 'app' as const, label: 'Bewerbung', icon: ClipboardList, count: 0 },
                      { key: 'interviews' as const, label: 'Interviews', icon: Video, count: detail.pipeline?.length || 0 },
                    ]).map((t) => {
                      const active = activeTab === t.key;
                      const Icon = t.icon;
                      return (
                        <button
                          key={t.key}
                          onClick={() => setActiveTab(t.key)}
                          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-semibold transition ${active ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {t.label}
                          {t.count > 0 && (
                            <span className={`rounded-full px-1.5 text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>{t.count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* ── Tab: Bewerbung (Screening) ── */}
                  {activeTab === 'app' && (
                    <div className="space-y-3">
                      {detail.summary && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Zusammenfassung</div>
                          <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{detail.summary}</p>
                        </div>
                      )}

                      {((detail.form_answers && Object.keys(detail.form_answers).length > 0) ||
                        (detail.documents && Object.keys(detail.documents).length > 0)) && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Bewerber-Angaben</div>
                          {detail.form_answers && Object.keys(detail.form_answers).length > 0 && (
                            <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                              {Object.entries(detail.form_answers).map(([k, v]) => (
                                <div key={k} className="text-[13px]">
                                  <dt className="text-[11px] text-slate-400">{FIELD_LABELS[k] || k}</dt>
                                  <dd className="font-medium text-slate-800">{v}</dd>
                                </div>
                              ))}
                            </dl>
                          )}
                          {detail.documents && Object.keys(detail.documents).length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {Object.entries(detail.documents).map(([k, d]) => (
                                <button
                                  key={k}
                                  type="button"
                                  onClick={() => downloadDocument(detail.id, k, d?.filename)}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-100"
                                >
                                  <FileText className="h-3.5 w-3.5 text-slate-400" /> {DOC_LABELS[k] || k}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {(detail.strengths || detail.gaps) && (
                        <Collapsible title="Stärken & Lücken">
                          <div className="space-y-3">
                            {detail.strengths && <Section title="Stärken" body={detail.strengths} />}
                            {detail.gaps && <Section title="Lücken" body={detail.gaps} />}
                          </div>
                        </Collapsible>
                      )}

                      {detail.missing_info && (
                        <Collapsible title="Im Interview prüfen">
                          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{detail.missing_info}</p>
                        </Collapsible>
                      )}

                      {detail.detailed_feedback && (
                        <Collapsible title="Recruiter-Feedback">
                          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{detail.detailed_feedback}</p>
                        </Collapsible>
                      )}

                      {Array.isArray(detail.criteria_scores) && detail.criteria_scores.length > 0 && (
                        <Collapsible title="Kriterien im Detail" subtitle={`${detail.criteria_scores.length} bewertet`}>
                          {detail.criteria_scores.map((c, i) => (
                            <div key={i} className="border-t border-slate-100 py-2 text-[13px] first:border-t-0 first:pt-0">
                              <div className="font-medium text-slate-800">
                                <span className="mr-1 font-bold">[{c.score}/5]</span>{c.label}
                                {c.weight != null && <span className="ml-1 text-slate-400">(w={c.weight})</span>}
                              </div>
                              {c.justification && <div className="mt-0.5 text-slate-600">{c.justification}</div>}
                              {Array.isArray(c.evidence) && c.evidence.map((e, j) => (
                                <div key={j} className="mt-0.5 text-[12px] italic text-slate-400">· "{e}"</div>
                              ))}
                            </div>
                          ))}
                        </Collapsible>
                      )}

                      {/* Recruiter-Entscheidung – aufklappbar, offen bei Human-Review */}
                      <Collapsible
                        title="Recruiter-Entscheidung"
                        subtitle="Stage, Override & Notizen"
                        defaultOpen={!!detail.needs_human_review}
                      >
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <label className="block">
                            <span className="mb-1 block text-xs font-semibold text-slate-600">Stage</span>
                            <select
                              value={form.stage}
                              onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                            >
                              {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-xs font-semibold text-slate-600">Empfehlung überschreiben</span>
                            <select
                              value={form.override_recommendation}
                              onChange={(e) => setForm((f) => ({ ...f, override_recommendation: e.target.value }))}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                            >
                              <option value="">(KI behalten)</option>
                              {RECS.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                            </select>
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-xs font-semibold text-slate-600">Score überschreiben (optional)</span>
                            <input
                              type="number" min={0} max={100}
                              value={form.override_score}
                              onChange={(e) => setForm((f) => ({ ...f, override_score: e.target.value }))}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                            />
                          </label>
                        </div>
                        <label className="mt-3 block">
                          <span className="mb-1 block text-xs font-semibold text-slate-600">Notizen</span>
                          <textarea
                            value={form.recruiter_notes}
                            onChange={(e) => setForm((f) => ({ ...f, recruiter_notes: e.target.value }))}
                            className="min-h-[80px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                          />
                        </label>
                        <button
                          onClick={saveReview}
                          disabled={saving}
                          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                        >
                          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                          {detail.reviewed ? 'Erneut speichern' : 'Als geprüft markieren'}
                        </button>
                      </Collapsible>
                    </div>
                  )}

                  {/* ── Tab: Interviews (Pipeline) ── */}
                  {activeTab === 'interviews' && (
                    <div className="space-y-3">
                      {(!detail.pipeline || detail.pipeline.length === 0) ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
                          <Video className="mx-auto mb-2 h-7 w-7 text-slate-300" />
                          <p className="text-[13px] font-medium text-slate-600">Noch keine Interviews</p>
                          <p className="mt-1 text-[12px] leading-relaxed text-slate-400">
                            Lade unten direkt zu einem Interview ein – oder konfiguriere eine feste Pipeline
                            unter „Jobs &amp; Career-Links“ → Bearbeiten.
                          </p>
                        </div>
                      ) : (
                        detail.pipeline.map((st) => {
                          const started = !!st.status && STARTED_STATUSES.includes(st.status);
                          const url = stageInviteUrl[st.stage_key];
                          const busy = stageBusy === st.stage_key;
                          return (
                            <div key={st.stage_key} className="rounded-2xl border border-slate-200 bg-white p-4">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase ${st.kind === 'warm_intro' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'}`}>
                                  {st.kind === 'warm_intro' ? 'Warm Intro' : 'Fach'}
                                </span>
                                <span className="text-[13px] font-semibold text-slate-800">{st.label}</span>
                                {st.adhoc && (
                                  <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700">Ad-hoc</span>
                                )}
                                {st.gate_mode === 'auto' && (
                                  <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                                    <Zap className="h-2.5 w-2.5" /> Auto ab {st.auto_threshold ?? 75}
                                  </span>
                                )}
                                <div className="ml-auto flex items-center gap-2">
                                  {st.status && (
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${st.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                      {WARM_INTRO_STATUS_LABEL[st.status] || st.status}
                                    </span>
                                  )}
                                  {st.overall_score != null && (
                                    <span className="text-xs font-semibold text-slate-700">{st.overall_score}/100</span>
                                  )}
                                </div>
                              </div>

                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                {!st.session_id && (
                                  <button
                                    onClick={() => inviteStage(st.stage_key)}
                                    disabled={busy || !detail.candidate_email}
                                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm shadow-pink-500/20 transition hover:shadow-md hover:shadow-pink-500/30 disabled:opacity-50"
                                  >
                                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                    <Mail className="h-3.5 w-3.5" /> Einladen
                                  </button>
                                )}
                                {st.session_id && started && (
                                  <button
                                    onClick={() => navigate(`/interviews/${st.session_id}`)}
                                    className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-700"
                                  >
                                    <Video className="h-3.5 w-3.5" /> Interview ansehen
                                    <ExternalLink className="h-3 w-3 opacity-70" />
                                  </button>
                                )}
                                {st.session_id && !started && (
                                  <>
                                    <button
                                      onClick={() => inviteStage(st.stage_key)}
                                      disabled={busy}
                                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                                    >
                                      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                      Einladung erneut senden
                                    </button>
                                    <span className="text-[12px] text-slate-500">Wartet auf den Kandidaten.</span>
                                  </>
                                )}
                              </div>

                              {st.gate_mode === 'auto' && !st.session_id && (
                                <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
                                  Auto-Gate aktiv – wird bei Score ≥ {st.auto_threshold ?? 75} automatisch eingeladen. Du kannst trotzdem jederzeit manuell einladen.
                                </p>
                              )}

                              {url && (
                                <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                                  <code className="flex-1 truncate rounded bg-white px-2 py-1 text-[12px] text-slate-600">{url}</code>
                                  <button
                                    onClick={() => { navigator.clipboard?.writeText(url); setCopiedStage(st.stage_key); setTimeout(() => setCopiedStage(null), 1500); }}
                                    className="shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-100"
                                  >
                                    {copiedStage === st.stage_key ? 'Kopiert' : 'Kopieren'}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}

                      {/* Ad-hoc invite: any template, no configured stage needed */}
                      {!showAdhoc ? (
                        <button
                          onClick={() => setShowAdhoc(true)}
                          className="flex w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 text-[13px] font-semibold text-slate-600 transition hover:border-slate-400 hover:bg-slate-50"
                        >
                          <Plus className="h-4 w-4" />
                          {(detail.pipeline && detail.pipeline.length > 0) ? 'Weiteres Interview einladen' : 'Zu Interview einladen'}
                        </button>
                      ) : (
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="mb-2 text-sm font-semibold text-slate-800">Interview auswählen</div>
                          <select
                            value={adhocTemplate}
                            onChange={(e) => setAdhocTemplate(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                          >
                            <option value="">Template wählen…</option>
                            {warmIntroTemplates.length > 0 && (
                              <optgroup label="Warm Intro">
                                {warmIntroTemplates.map((t) => (
                                  <option key={t.id} value={t.id}>{cleanTemplateTitle(t.title)} · {t.language?.toUpperCase()}</option>
                                ))}
                              </optgroup>
                            )}
                            {ownFachTemplates.length > 0 && (
                              <optgroup label="★ Meine Fach-Interviews">
                                {ownFachTemplates.map((t) => (
                                  <option key={t.id} value={t.id}>{cleanTemplateTitle(t.title)} · {t.language?.toUpperCase()}</option>
                                ))}
                              </optgroup>
                            )}
                            {ceveeFachTemplates.length > 0 && (
                              <optgroup label="Cevee-Bibliothek">
                                {ceveeFachTemplates.map((t) => (
                                  <option key={t.id} value={t.id}>{cleanTemplateTitle(t.title)} · {t.language?.toUpperCase()}</option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                          <div className="mt-3 flex items-center gap-2">
                            <button
                              onClick={inviteAdhoc}
                              disabled={adhocBusy || !adhocTemplate || !detail.candidate_email}
                              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm shadow-pink-500/20 transition hover:shadow-md hover:shadow-pink-500/30 disabled:opacity-50"
                            >
                              {adhocBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                              Einladen
                            </button>
                            <button
                              onClick={() => { setShowAdhoc(false); setAdhocTemplate(''); }}
                              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100"
                            >
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </RecruiterPageShell>
  );
};

const Section: React.FC<{ title: string; body?: string }> = ({ title, body }) => (
  <div>
    <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</div>
    <div className="mt-0.5 whitespace-pre-wrap text-[13px] text-slate-700">{body || '-'}</div>
  </div>
);

const Collapsible: React.FC<{
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition hover:bg-slate-50"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800">{title}</div>
          {subtitle && <div className="truncate text-[11px] text-slate-400">{subtitle}</div>}
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="border-t border-slate-100 px-4 py-3">{children}</div>}
    </div>
  );
};

// Three-way toggle (Aus / Optional / Pflicht) for one configurable apply-form field.
const FormFieldToggle: React.FC<{
  label: string;
  mode: 'off' | ApplyFieldMode;
  onChange: (mode: 'off' | ApplyFieldMode) => void;
}> = ({ label, mode, onChange }) => {
  const opts: { v: 'off' | ApplyFieldMode; l: string }[] = [
    { v: 'off', l: 'Aus' },
    { v: 'optional', l: 'Optional' },
    { v: 'required', l: 'Pflicht' },
  ];
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={`text-[12px] ${mode === 'off' ? 'text-slate-500' : 'font-medium text-slate-800'}`}>{label}</span>
      <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-slate-200">
        {opts.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`px-2 py-1 text-[11px] font-semibold transition ${
              mode === o.v
                ? o.v === 'off'
                  ? 'bg-slate-200 text-slate-600'
                  : o.v === 'required'
                    ? 'bg-slate-800 text-white'
                    : 'bg-emerald-600 text-white'
                : 'bg-white text-slate-500 hover:bg-slate-50'
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Applications;
